// src/components/Sidebar.jsx

import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  FileText,
  Calendar,
  IndianRupee,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  X,
  CalendarDays,
  Monitor,
  Clock,
  BarChart3,
  UserRoundCog,
  Target,
  Trophy,
  UsersRound,
  TrendingUp,
  Receipt,
  FolderOpen,
  Briefcase,
  Home,
  BookOpen,
  Building2,
  User,
} from "lucide-react";
import { usePermission } from "@/contexts/RoleAccessContext";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { isLinkedAdmin } from "@/utils/workspace";
import { useTeam } from "@/contexts/TeamContext";
import useIsMobile from "@/hooks/useIsMobile";
import { BACK_EVENT } from "@/hooks/useAndroidBackButton";

/* Colour treatment mirrors the LEARN sidebar: one dark surface in both themes,
   muted foreground, white on hover, and a translucent teal accent wash + hairline
   ring on the active item. The tokens themselves shift between light/dark, so
   these classes deliberately carry no `dark:` overrides. */
const SIDEBAR_ICON_BTN =
  "text-sidebar-muted hover:bg-white/10 hover:text-white";

const SIDEBAR_ITEM_IDLE =
  "text-sidebar-foreground/90 hover:bg-white/[0.06] hover:text-white";

const SIDEBAR_ITEM_ACTIVE =
  "bg-sidebar-accent/15 text-white hover:bg-sidebar-accent/20 hover:text-white" +
  "shadow-[inset_0_0_0_1px_hsl(var(--sidebar-accent)/0.25)]";

const SIDEBAR_SUBITEM_ACTIVE = SIDEBAR_ITEM_ACTIVE;

const SIDEBAR_LOGOUT =
  "text-sidebar-foreground/90 hover:bg-white/[0.06] hover:text-white";

const allMenuItems = [
  {
    title: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    badge: null,
    featureRequired: null, // Always visible
  },
  {
    title: "Employees",
    href: "/employees",
    icon: Users,
    badge: null,
    featureRequired: null,
    permission: {
      feature: "employees",
      actions: ["view", "create", "edit", "delete"],
      allowAdmin: true,
    },
  },
  {
    title: "Payroll",
    icon: CreditCard,
    permission: {
      feature: ["run_payroll", "salary_structure", "payslips"],
      actions: ["view"],
      allowAdmin: true,
    },
    items: [
      {
        title: "Run Payroll",
        href: "/payroll/run",
        icon: CreditCard,
        permission: {
          feature: "run_payroll",
          actions: ["view"],
          allowAdmin: true,
        },
      },
      {
        title: "Salary Structure",
        href: "/payroll/salary-structure",
        icon: CreditCard,
        permission: {
          feature: "salary_structure",
          actions: ["view"],
          allowAdmin: true,
        },
      },
      {
        title: "Payslips",
        href: "/payslips",
        icon: FileText,
        badge: null,
        permission: {
          feature: "payslips",
          actions: ["view", "generate_bank_advice", "email_send"],
          allowAdmin: true,
        },
      },
    ],
  },
  {
    title: "Attendance",
    icon: Calendar,
    items: [
      {
        title: "Dashboard",
        href: "/attendance/dashboard",
        icon: LayoutDashboard,
        permission: {
          feature: "attendance_dashboard",
          actions: ["view"],
          allowAdmin: true,
        },
      },
      {
        title: "Regularization",
        href: "/attendance/regularization",
        icon: FileText,
        featureRequired: "regularization_requests",
        permission: {
          feature: "regularization_requests",
          actions: ["view", "edit"],
          allowAdmin: true,
        },
      },
      {
        title: "Shifts & Overtime",
        href: "/attendance/shifts",
        icon: Clock,
        permission: {
          feature: [
            "shifts",
            "overtime_management",
            "rotational_shift_schedules",
          ],
          actions: ["view"],
          allowAdmin: true,
        },
      },
      {
        title: "Reports",
        href: "/attendance/reports",
        icon: BarChart3,
        permission: {
          feature: "attendance_reports",
          actions: ["view"],
          allowAdmin: true,
        },
      },
    ],
  },
  {
    title: "Leave Management",
    href: "/leave-management",
    icon: Calendar,
    badge: null,
    feature: "basic_leave_management",
    featureOr: "custom_leave_management",
    permission: {
      feature: "leave_management",
      actions: ["view", "create", "edit"],
      allowAdmin: true,
    },
  },
  {
    title: "Loan Management",
    href: "/loan-management",
    icon: IndianRupee,
    badge: null,
    featureRequired: "loan_management",
    permission: {
      feature: "loan_management",
      actions: ["view", "create", "edit"],
      allowAdmin: true,
    },
  },
  {
    title: "Reimbursements",
    href: "/reimbursement-management",
    icon: Receipt,
    badge: null,
    featureRequired: "advanced_payroll",
    permission: {
      feature: "reimbursement_management",
      actions: ["view", "create", "edit"],
      allowAdmin: true,
    },
  },
  {
    title: "Login Details",
    href: "/admin/login-details",
    icon: Monitor,
    badge: null,
    featureRequired: "employee_portal",
    permission: {
      feature: "login_details",
      actions: ["view"],
      allowAdmin: true,
    },
  },
  {
    title: "Event Management",
    href: "/admin/events",
    icon: CalendarDays,
    badge: null,
    featureRequired: "event_management",
    permission: {
      feature: "event_management",
      actions: ["view", "create", "edit", "delete"],
      allowAdmin: true,
    },
  },
  {
    title: "Company Documents",
    href: "/company-documents",
    icon: FolderOpen,
    badge: null,
    featureRequired: null, // Available on every plan
    permission: {
      feature: "company_documents",
      actions: ["view"],
      allowAdmin: true,
    },
  },
  {
    title: "Performance",
    icon: Trophy,
    featureRequired: "performance_management",
    permission: {
      feature: ["performance_management", "goals_okrs", "template_builder"],
      actions: ["view"],
      allowAdmin: true,
    },
    items: [
      {
        title: "Dashboard",
        href: "/performance",
        icon: LayoutDashboard,
        featureRequired: "performance_management",
        permission: {
          feature: "performance_management",
          actions: ["view"],
          allowAdmin: true,
        }
      },
      {
        title: "Goals & OKRs",
        href: "/performance/goals",
        icon: Target,
        featureRequired: "performance_management",
        permission: {
          feature: "goals_okrs",
          actions: ["view", "create", "edit", "delete"],
          allowAdmin: true,
        }
      },
      {
        title: "Reviews",
        href: "/performance/my-appraisals",
        icon: FileText,
        featureRequired: "performance_management",
        permission: {
          feature: "performance_management",
          actions: ["view", "review", "approve"],
          allowAdmin: true,
        },
      },
      {
        title: "Template Builder",
        href: "/template-builder",
        icon: UserRoundCog,
        featureRequired: "performance_management",
        permission: {
          feature: "template_builder",
          actions: ["view", "create", "edit", "delete"],
          allowAdmin: true,
        }
      }
    ],
  },
  {
    title: "Analytics & Logs",
    icon: Monitor,
    featureRequired: "payroll_analytics",
    featureOr: "activity_logs",
    items: [
      {
        title: "Payroll Analytics",
        href: "/payroll-analytics",
        icon: Monitor,
        featureRequired: "payroll_analytics",
        permission: {
          feature: "payroll_analytics",
          actions: ["view"],
          allowAdmin: true,
        },
      },
      {
        title: "Activity Logs",
        href: "/activity-logs",
        icon: Monitor,
        featureRequired: "activity_logs",
        permission: {
          feature: "activity_logs",
          actions: ["view"],
          allowAdmin: true,
        },
      },
    ],
  },
  {
    title: "Settings",
    icon: Settings,
    featureRequired: null, // Always visible
    items: [
      {
        title: "General Settings",
        href: "/settings",
        icon: Settings,
        featureRequired: null,
        permission: {
          feature: [
            "settings_company_configuration",
            "attendance_and_policy_configuration",
            "settings_notification_configuration",
            "settings_security_configuration",
            "settings_bank_configuration",
            "settings_loan_configuration",
            "settings_leave_configuration",
            "work_locations_configuration",
            "department_configuration",
            "designation_configuration",
          ],
          actions: ["view"],
          allowAdmin: true,
        },
      },
      {
        title: "Salary Components",
        href: "/salary-components",
        icon: CreditCard,
        featureRequired: "advanced_payroll",
        permission: {
          feature: "salary_components_configuration",
          actions: ["view", "create", "edit", "delete"],
          allowAdmin: true,
        },
      },
      {
        title: "Tax Configuration",
        href: "/tax-configuration",
        icon: FileText,
        featureRequired: "advanced_payroll",
        permission: {
          feature: "tax_configuration",
          actions: ["view", "create", "edit", "delete"],
          allowAdmin: true,
        },
      },
      {
        title: "Leave Configuration",
        href: "/leave-configuration",
        icon: CreditCard,
        featureRequired: "basic_leave_management",
        featureOr: "custom_leave_management",
        permission: {
          feature: ["settings_leave_configuration", "holiday_configuration"],
          actions: ["view", "create", "edit", "delete"],
          allowAdmin: true,
        },
      },
      {
        title: "Users & Roles",
        href: "/permission",
        icon: UserRoundCog,
        featureRequired: "roles_and_access",
        permission: {
          feature: "roles_and_access",
          actions: ["view", "create", "edit", "delete"],
          allowAdmin: true,
        },
      },
      {
        title: "Subscription",
        href: "/upgrade",
        icon: Receipt,
        featureRequired: null,
        permission: {
          feature: "subscription_management",
          actions: ["edit"],
          allowAdmin: true,
        },
      }
    ],
  },
];

// An admin who is also an employee of the company gets their own pages too —
// the same ones the employee portal shows — in one combined sidebar.
const myWorkspaceGroup = {
  title: "My workspace",
  icon: Briefcase,
  activePrefix: "/employee/",
  items: [
    { title: "Overview", href: "/employee/dashboard", icon: Home },
    { title: "Attendance", href: "/employee/attendance", icon: Clock },
    {
      title: "Leave",
      href: "/employee/leave",
      icon: Calendar,
      featureRequired: "basic_leave_management",
      featureOr: "custom_leave_management",
    },
    { title: "Payslips", href: "/employee/payslips", icon: FileText },
    { title: "Finances", href: "/employee/finances", icon: IndianRupee },
    {
      title: "Reviews",
      href: "/employee/reviews",
      icon: BookOpen,
      featureRequired: "performance_management",
    },
    {
      title: "My Documents",
      href: "/employee/documents",
      icon: FolderOpen,
      featureRequired: "document_upload",
    },
    { title: "Company Documents", href: "/employee/company-documents", icon: Building2 },
    { title: "Profile", href: "/employee/profile", icon: User },
  ],
};

// Anyone who manages people (the reporting line, not the role) gets their
// team's pages — those the company's manager duties turn on.
const myTeamGroup = (duties) => ({
  title: "My team",
  icon: UsersRound,
  activePrefix: "/team/",
  items: [
    duties.leave && {
      title: "Leave approvals",
      href: "/team/leave",
      icon: Calendar,
      featureRequired: "basic_leave_management",
      featureOr: "custom_leave_management",
    },
    (duties.appraisals || duties.pips) && {
      title: "Reviews",
      href: "/team/reviews",
      icon: BookOpen,
      featureRequired: "performance_management",
    },
    duties.goals && {
      title: "Goals",
      href: "/team/goals",
      icon: Target,
      featureRequired: "performance_management",
    },
  ].filter(Boolean),
});

const Sidebar = ({ collapsed, onToggle, mobileOpen = false, onMobileClose }) => {
  // Phones: an off-canvas drawer (always full width with labels) opened from
  // the top bar; tablets and up: the usual rail / panel.
  const isMobile = useIsMobile();
  const showCollapsed = collapsed && !isMobile;
  const closeMobileNav = () => onMobileClose?.();
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = React.useState(null);
  const { logout, user } = useAuth();
  const {
    hasFeature,
    loading: subscriptionLoading, planId,
  } = useSubscription();
  const [notificationCounts, setNotificationCounts] = useState({});
  const { can } = usePermission();

  const roles = Array.isArray(user?.role)
    ? user.role
    : [user?.role].filter(Boolean);

  // -----------------------------
  // Permission + Subscription filter (readable)
  // -----------------------------
  const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

  const hasSubscriptionAccess = (item) => {
    if (subscriptionLoading) return true;

    // Block if plan disallowed
    if (item.disallowedPlans && item.disallowedPlans.includes(planId)) {
      return false;
    }

    // No feature required => allowed
    if (!item.featureRequired) return true;

    // featureRequired OR featureOr
    let ok = hasFeature(item.featureRequired);
    if (!ok && item.featureOr) ok = hasFeature(item.featureOr);

    return Boolean(ok);
  };

  // Supports either can("feature", "action") OR can({ feature, action })
  const safeCan = (feature, action) => {
    if (typeof can !== "function") return false;

    try {
      return Boolean(can(feature, action));
    } catch (e1) {
      try {
        return Boolean(can({ feature, action }));
      } catch (e2) {
        return false;
      }
    }
  };

  const hasPermissionAccess = (item) => {
    const perm = item.permission;
    if (!perm) return true; // No RBAC restriction

    const requiredFeatures = toArray(perm.feature);
    const requiredActions = toArray(perm.actions);

    // Show item if user can do ANY required action on ANY required feature
    for (const f of requiredFeatures) {
      for (const a of requiredActions) {
        if (safeCan(f, a)) return true;
      }
    }

    return false;
  };

  const filterMenuItems = (items) => {
    if (!Array.isArray(items)) return [];

    // Step 1: filter children first (recursively)
    const withFilteredChildren = items.map((item) => {
      if (!item.items) return item;

      const filteredKids = filterMenuItems(item.items);
      return { ...item, items: filteredKids };
    });

    // Step 2: decide visibility
    return withFilteredChildren.filter((item) => {
      const subscriptionOk = hasSubscriptionAccess(item);
      const permissionOk = hasPermissionAccess(item);

      if (item.items) {
        // Parent shows only if it has visible children AND passes checks
        return item.items.length > 0 && subscriptionOk && permissionOk;
      }

      // Leaf must pass checks
      return subscriptionOk && permissionOk;
    });
  };

  const linkedAdmin = isLinkedAdmin(user);
  const team = useTeam();
  const menuItems = useMemo(
    () => {
      const [dashboard, ...rest] = allMenuItems;
      return filterMenuItems([
        dashboard,
        ...(linkedAdmin ? [myWorkspaceGroup] : []),
        ...(team.isManager ? [myTeamGroup(team.duties)] : []),
        ...rest,
      ]);
    },
    [subscriptionLoading, planId, hasFeature, can, user?.role, linkedAdmin, team.isManager, team.duties],
  );

  // -----------------------------
  // Notifications mapping (unchanged)
  // -----------------------------
  const getNotificationCountForRoute = (href) => {
    const routeCategoryMap = {
      "/attendance": ["ot", "overtime", "ot_log"],
      "/leave-management": ["leave"],
      "/loan-management": ["loan"],
      "/payslips": ["payslip"],
      "/employees": ["employee", "profile", "document", "birthday"],
    };

    const categories = routeCategoryMap[href] || [];
    let total = 0;

    categories.forEach((cat) => {
      total += notificationCounts[cat] || 0;
    });

    return total > 0 ? total : null;
  };

  const handleLogout = () => {
    logout();
  };

  const isActiveLink = (href) => {
    return location.pathname === href;
  };

  const isActiveSubmenu = (items) => {
    return items?.some((item) => location.pathname === item.href);
  };

  // A group is active on any of its pages — including ones without their own
  // entry (e.g. a review opened from My workspace → Reviews).
  const isActiveGroup = (item) =>
    item.activePrefix
      ? location.pathname.startsWith(item.activePrefix)
      : isActiveSubmenu(item.items);

  const toggleSubmenu = (index) => {
    if (showCollapsed) return;
    setOpenSubmenu(openSubmenu === index ? null : index);
  };

  useEffect(() => {
    // Auto-open submenu if current path matches
    menuItems.forEach((item, index) => {
      if (item.items && isActiveGroup(item)) {
        setOpenSubmenu(index);
      }
    });
  }, [location.pathname, showCollapsed]);

  // Android's back button closes the open drawer before leaving the page.
  useEffect(() => {
    if (!isMobile || !mobileOpen) return undefined;
    const onBack = (event) => {
      event.preventDefault();
      closeMobileNav();
    };
    window.addEventListener(BACK_EVENT, onBack);
    return () => window.removeEventListener(BACK_EVENT, onBack);
  }, [isMobile, mobileOpen]);

  // The drawer closes once you've picked a page.
  useEffect(() => {
    if (isMobile) closeMobileNav();
  }, [location.pathname]);

  return (
    <>
    {isMobile && mobileOpen && (
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={closeMobileNav}
        aria-hidden="true"
      />
    )}
    <div
      className={cn(
        "fixed left-0 top-0 z-40 h-full bg-sidebar-surface text-sidebar-foreground border-r border-sidebar-border transition-all duration-300 ease-in-out",
        showCollapsed ? "w-[76px]" : "w-[264px]",
        isMobile && "z-50 shadow-2xl",
        isMobile && !mobileOpen && "-translate-x-full",
        "flex flex-col",
      )}
      // The drawer runs edge to edge, so it keeps clear of the notch and the
      // home indicator itself.
      style={
        isMobile
          ? {
              paddingTop: "var(--safe-area-inset-top)",
              paddingBottom: "var(--safe-area-inset-bottom)",
            }
          : undefined
      }
      aria-hidden={isMobile && !mobileOpen ? true : undefined}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-sidebar-border">
        {!showCollapsed && (
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-3">
              <div className="shrink-0 rounded-lg bg-transparent p-1 dark:p-0">
                <img
                  src="/zenith-logo-no-name.png"
                  alt="Zenith Logo"
                  className="w-auto h-8"
                />
              </div>
              <div>
                <h1 className="font-bold text-lg text-white">
                  Zenith
                </h1>
                <p className="text-xs text-sidebar-muted">
                  Payroll Management
                </p>
              </div>
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={isMobile ? closeMobileNav : onToggle}
          className={cn("h-8 w-8", SIDEBAR_ICON_BTN)}
          data-testid="sidebar-toggle-btn"
          aria-label={isMobile ? "Close menu" : showCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isMobile ? (
            <X className="h-4 w-4" />
          ) : showCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation (scrollable) */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-2">
          {menuItems.map((item, index) => {
            const isActive = item.href
              ? isActiveLink(item.href)
              : isActiveGroup(item);

            const hasSubmenu = !!item.items;
            const isSubmenuOpen = openSubmenu === index;
            const Icon = item.icon;

            return (
              <div key={index}>
                {item.href ? (
                  // Single menu item
                  <Link to={item.href}>
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      className={cn(
                        "w-full justify-start h-10 px-3",
                        isActive && SIDEBAR_ITEM_ACTIVE,
                        !isActive && SIDEBAR_ITEM_IDLE,
                        showCollapsed && "justify-center px-0",
                      )}
                      data-testid={`sidebar-${item.title
                        .toLowerCase()
                        .replace(/\s+/g, "-")}-link`}
                    >
                      <Icon
                        className={cn(
                          "h-4 w-4",
                          !showCollapsed && "mr-3",
                          isActive && "text-sidebar-accent",
                        )}
                      />

                      {!showCollapsed && (
                        <>
                          <span className="flex-1 text-left">{item.title}</span>

                          {(() => {
                            const notifCount = getNotificationCountForRoute(
                              item.href,
                            );
                            const badge = item.badge;

                            if (notifCount) {
                              return (
                                <Badge variant="destructive" className="ml-2">
                                  {notifCount}
                                </Badge>
                              );
                            }

                            if (badge) {
                              return (
                                <Badge
                                  variant={badge.variant || "secondary"}
                                  className="ml-2"
                                >
                                  {badge.text}
                                </Badge>
                              );
                            }

                            return null;
                          })()}
                        </>
                      )}
                    </Button>
                  </Link>
                ) : (
                  // Menu item with submenu
                  <>
                    {/* Expanded sidebar */}
                    {!showCollapsed && (
                      <Button
                        variant={isActive ? "secondary" : "ghost"}
                        className={cn(
                          "w-full justify-start h-10 px-3",
                          isActive && SIDEBAR_ITEM_ACTIVE,
                          !isActive && SIDEBAR_ITEM_IDLE,
                        )}
                        onClick={() => toggleSubmenu(index)}
                      >
                        <Icon
                          className={cn(
                            "h-4 w-4 mr-3",
                            isActive && "text-sidebar-accent",
                          )}
                        />
                        <span className="flex-1 text-left">{item.title}</span>

                        <ChevronRight
                          className={cn(
                            "h-4 w-4 transition-transform duration-200",
                            isSubmenuOpen && "rotate-90",
                          )}
                        />
                      </Button>
                    )}

                    {/* Collapsed sidebar → popover submenu */}
                    {showCollapsed && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={isActive ? "secondary" : "ghost"}
                            className={cn(
                              "w-full justify-center h-10",
                              isActive ? SIDEBAR_ITEM_ACTIVE : SIDEBAR_ITEM_IDLE,
                            )}
                          >
                            <Icon
                              className={cn(
                                "h-4 w-4",
                                isActive && "text-sidebar-accent",
                              )}
                            />
                          </Button>
                        </PopoverTrigger>

                        <PopoverContent className="w-56 p-2">
                          <div className="space-y-1">
                            <p className="text-xs font-semibold mb-1 text-muted-foreground">
                              {item.title}
                            </p>

                            {item.items.map((subItem, subIndex) => {
                              const SubIcon = subItem.icon;
                              const isSubActive = isActiveLink(subItem.href);

                              return (
                                <Link key={subIndex} to={subItem.href}>
                                  <Button
                                    variant={
                                      isSubActive ? "secondary" : "ghost"
                                    }
                                    className="w-full justify-start h-9 text-sm"
                                  >
                                    <SubIcon className="h-3 w-3 mr-2" />
                                    {subItem.title}
                                  </Button>
                                </Link>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}

                    {/* Submenu content (only when expanded) */}
                    {!showCollapsed && isSubmenuOpen && (
                      <div className="ml-4 mt-1 space-y-1 animate-slide-in">
                        {item.items.map((subItem, subIndex) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = isActiveLink(subItem.href);

                          return (
                            <Link key={subIndex} to={subItem.href}>
                              <Button
                                variant={isSubActive ? "secondary" : "ghost"}
                                className={cn(
                                  "w-full justify-start h-9 px-3 text-sm",
                                  isSubActive
                                    ? SIDEBAR_SUBITEM_ACTIVE
                                    : SIDEBAR_ITEM_IDLE,
                                )}
                              >
                                <SubIcon
                                  className={cn(
                                    "h-3 w-3 mr-2",
                                    isSubActive && "text-sidebar-accent",
                                  )}
                                />
                                {subItem.title}
                              </Button>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="mt-auto p-3 border-t border-sidebar-border">
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start h-10 px-3",
            SIDEBAR_LOGOUT,
            showCollapsed && "justify-center px-0",
          )}
          onClick={handleLogout}
          data-testid="sidebar-logout-btn"
        >
          <LogOut className={cn("h-4 w-4", !showCollapsed && "mr-3")} />
          {!showCollapsed && "Logout"}
        </Button>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
